import {useState, useCallback} from 'react'
import {useI18n} from '@/i18n'
import {CompletionEditor, type Suggestion} from '@/components/ui/completion-editor'
import {useCelEngine, useProfileCelEngine} from '@/lib/cel/use-cel-engine'
import type {CelSuggestion} from '@/lib/cel/ruleengine/cel/CelSuggestion'
import type {IamCelSuggestionEngine} from '@/lib/cel/ruleengine/cel/IamCelSuggestionEngine'

interface ExpressionEditorProps {
    value: string
    onChange: (value: string) => void
    placeholder?: string
    className?: string
    rows?: number
    /**
     * Roots the suggestion engine should restrict to (issue 654 Gap 3).
     * Required, non-empty. Pass {@code ['any']} for schema policies (no
     * restriction — IAM multi-root schema), {@code ['profile']} for
     * restriction rules, or specific root names like {@code ['identity']}.
     */
    schemaTypes: string[]
    /**
     * Factory that resolves the suggestion engine for the supplied
     * {@code schemaTypes}. Defaults to the built-in profile/IAM two-branch, so
     * existing callers stay unchanged. Inject a different factory (e.g.
     * {@code useCertificationCelEngine}) to drive the editor over another
     * schema root (issue 924.05 / spec §8.2).
     */
    getEngine?: (schemaTypes: string[]) => IamCelSuggestionEngine
    'data-testid'?: string
}

const KIND_TO_SUGGESTION_KIND: Partial<Record<CelSuggestion['kind'], Suggestion['kind']>> = {
    STATIC_FIELD: 'STATIC_FIELD',
    ATTRIBUTE: 'ATTRIBUTE',
    RELATIONSHIP_ALIAS: 'RELATIONSHIP_ALIAS',
}

function toLocalSuggestion(s: CelSuggestion): Suggestion {
    const insertText = s.replacement?.text ?? s.text
    return {
        value: s.text,
        insertText,
        description: s.description ?? '',
        kind: KIND_TO_SUGGESTION_KIND[s.kind] ?? null,
        replaceStart: s.replacement?.start,
        replaceEnd: s.replacement?.end,
        replaceText: s.replacement?.text,
    }
}

export function ExpressionEditor({
                                     value,
                                     onChange,
                                     placeholder,
                                     className,
                                     rows = 6,
                                     schemaTypes,
                                     getEngine,
                                     'data-testid': dataTestId,
                                 }: ExpressionEditorProps) {
    const i18n = useI18n()
    const iamEngine = useCelEngine()
    const profileEngine = useProfileCelEngine()
    const defaultGetEngine = useCallback(
        (types: string[]): IamCelSuggestionEngine => (types.includes('profile') ? profileEngine : iamEngine),
        [iamEngine, profileEngine],
    )
    const engine: IamCelSuggestionEngine = (getEngine ?? defaultGetEngine)(schemaTypes)
    const [suggestions, setSuggestions] = useState<Suggestion[]>([])

    const handleSuggestionsRequested = useCallback((text: string, cursorPosition: number) => {
        // WHY: in-browser engine — synchronous, no debounce, no abort. The
        // engine builds the AST, locates the cursor, and dispatches in
        // microseconds. The pre-CEL implementation called the backend with a
        // 200ms debounce; that path is gone (see issue 514.07).
        try {
            const raw = engine.suggest(text, cursorPosition)
            setSuggestions(raw.map(toLocalSuggestion))
        } catch {
            // WHY: suggestion is a UX path, never an error path. A bad
            // sentinel injection or a half-parsed expression yields no
            // completions; never propagate to the toast/error surface.
            setSuggestions([])
        }
    }, [engine])

    const helpHint = (
        <div className="text-xs text-muted-foreground mt-1">
            <kbd className="px-1 py-0.5 bg-muted rounded text-xs">{i18n.expressionEditor.keyTab}</kbd> {i18n.expressionEditor.tabAccept},{' '}
            <kbd className="px-1 py-0.5 bg-muted rounded text-xs">{i18n.expressionEditor.keyEsc}</kbd> {i18n.expressionEditor.escDismiss},{' '}
            <kbd className="px-1 py-0.5 bg-muted rounded text-xs">{i18n.expressionEditor.keyCtrlZ}</kbd> {i18n.expressionEditor.ctrlZUndo},{' '}
            <kbd className="px-1 py-0.5 bg-muted rounded text-xs">{i18n.expressionEditor.keyCtrlShiftZ}</kbd> {i18n.expressionEditor.ctrlShiftZRedo}
        </div>
    )

    return (
        <CompletionEditor
            value={value}
            onChange={onChange}
            suggestions={suggestions}
            onSuggestionsRequested={handleSuggestionsRequested}
            placeholder={placeholder}
            className={className}
            rows={rows}
            textareaTestId="expression-editor-textarea" // eslint-disable-line i18next/no-literal-string -- test ID
            helpHint={helpHint}
            data-testid={dataTestId}
        />
    )
}
